import { useQueries } from '@tanstack/react-query';
import { getToolName, isToolUIPart } from 'ai';
import { ChevronRight } from 'lucide-react';
import { Fragment, useId, useState } from 'react';

import {
  Message,
  MessageContent,
  Task,
  TaskContent,
  TaskItem,
  TaskTrigger,
  ToolCallCard,
} from '@/components/ai-elements';
import { MarkdownMessage } from '@/components/miranda/MarkdownMessage';
import { cn } from '@/lib/utils';
import {
  cardActionTrailText,
  type MirandaUIMessage,
  type WorkflowPointer,
  type WriteToolResult,
} from '@/services/miranda-rich-chat';

import {
  BEFORE_DECISION_LABEL,
  ProposalCard,
  isTextFromBeforeDecision,
  isWriteToolResult,
  proposalStatusId,
  proposalStatusQuery,
} from './proposal-card';
import { ReservationCards, reservationsByToolCall, type ReservationSummary } from './reservation-card';
import { WorkflowApprovalCard } from './workflow-approval-card';

type Part = MirandaUIMessage['parts'][number];

/** Una tool call del missatge, amb el que les targetes en necessiten. */
export interface ToolView {
  id: string;
  name: string;
  state: string;
  input: unknown;
  output: unknown;
  errorText?: string;
}

/** Els trossos d'un missatge, en l'ordre en què s'han de pintar. */
export type MessageSegment =
  | { kind: 'text'; key: string; text: string }
  | { kind: 'tools'; key: string; tools: ToolView[] }
  | { kind: 'workflow'; key: string; pointer: WorkflowPointer }
  | { kind: 'card-action'; key: string; text: string };

/**
 * Les parts d'un missatge de `useChat` agrupades per pintar: el text, les tool calls seguides
 * (un sol bloc plegable, no una targeta per crida), els workflows que esperen aprovació i el
 * rastre d'una acció feta a una targeta. La resta de parts (`step-start`, raonament, fonts) no es
 * pinten.
 *
 * Un mateix `runId` només surt un cop: si el model crida dues vegades la tool, o l'stream repeteix
 * la part, no hi ha d'haver dues targetes amb els mateixos botons.
 */
export function segmentMessage(message: MirandaUIMessage): MessageSegment[] {
  const segments: MessageSegment[] = [];
  const seenRuns = new Set<string>();

  message.parts.forEach((part: Part, index) => {
    const last = segments[segments.length - 1];

    if (part.type === 'text') {
      // Xarxa de seguretat: un text d'usuari que comença pel prefix del rastre és un rastre,
      // vingui d'on vingui (l'historial ja el porta com a `data-card-action`).
      const trail = message.role === 'user' ? cardActionTrailText(part.text) : null;
      if (trail !== null) {
        if (trail) segments.push({ kind: 'card-action', key: `card-action-${index}`, text: trail });
        return;
      }
      if (part.text) segments.push({ kind: 'text', key: `text-${index}`, text: part.text });
      return;
    }
    if (isToolUIPart(part)) {
      const tool: ToolView = {
        id: part.toolCallId,
        name: getToolName(part),
        state: part.state,
        input: part.input,
        output: 'output' in part ? part.output : undefined,
        errorText: 'errorText' in part ? part.errorText : undefined,
      };
      if (last?.kind === 'tools') last.tools.push(tool);
      else segments.push({ kind: 'tools', key: `tools-${index}`, tools: [tool] });
      return;
    }
    if (part.type === 'data-workflow') {
      const pointer = part.data;
      if (pointer?.runId && !seenRuns.has(pointer.runId)) {
        seenRuns.add(pointer.runId);
        segments.push({ kind: 'workflow', key: `workflow-${pointer.runId}`, pointer });
      }
      return;
    }
    if (part.type === 'data-card-action') {
      const text = typeof part.data?.text === 'string' ? part.data.text.trim() : '';
      if (text) segments.push({ kind: 'card-action', key: `card-action-${index}`, text });
    }
  });

  return segments;
}

/** El títol del bloc de tool calls: què s'està fent ara, o què s'ha fet. */
export function toolsTitle(tools: ToolView[]): string {
  const running = tools.find((tool) => tool.state === 'input-streaming' || tool.state === 'input-available');
  if (running) return `Treballant: ${running.name}…`;
  const failed = tools.filter((tool) => tool.state === 'output-error').length;
  const names = [...new Set(tools.map((tool) => tool.name))].join(', ');
  const count = tools.length === 1 ? '1 acció' : `${tools.length} accions`;
  return `${count}${failed > 0 ? ` (${failed} amb error)` : ''}: ${names}`;
}

/** Les tools d'un missatge que ja han acabat i porten el resultat d'una escriptura (una targeta). */
export function writeResultsOf(segments: MessageSegment[]): WriteToolResult[] {
  return segments.flatMap((segment) =>
    segment.kind === 'tools'
      ? segment.tools.flatMap((tool) =>
          tool.state === 'output-available' && isWriteToolResult(tool.output) ? [tool.output] : [],
        )
      : [],
  );
}

/**
 * Les reserves que cada tool call del missatge ha de pintar, sense repetir-ne cap dins del
 * mateix missatge: surt a la darrera lectura, la més fresca (`reservationsByToolCall`). Només compten les lectures acabades: una tool
 * d'escriptura pinta la seva targeta de proposta, no reserves.
 */
export function messageReservations(segments: MessageSegment[]): Map<string, ReservationSummary[]> {
  const readTools = segments.flatMap((segment) =>
    segment.kind === 'tools'
      ? segment.tools.filter((tool) => tool.state === 'output-available' && !isWriteToolResult(tool.output))
      : [],
  );
  return reservationsByToolCall(readTools);
}

function ToolsBlock({
  tools,
  reservations,
  isStreaming,
}: {
  tools: ToolView[];
  reservations: Map<string, ReservationSummary[]>;
  isStreaming: boolean;
}) {
  return (
    <div className="min-w-0 max-w-full space-y-2">
      {/* Plegat: el xat s'ha de poder llegir sense JSON a la vista. */}
      <Task defaultOpen={false}>
        <TaskTrigger title={toolsTitle(tools)} />
        <TaskContent>
          {tools.map((tool) => (
            <TaskItem key={tool.id}>
              <ToolCallCard
                call={{
                  name: tool.name,
                  args: tool.input,
                  result: tool.output,
                  error: tool.errorText,
                }}
              />
            </TaskItem>
          ))}
        </TaskContent>
      </Task>

      {/* Les targetes surten del resultat de cada tool: una proposta, o les reserves llegides. */}
      {tools.map((tool) => {
        if (tool.state !== 'output-available') return null;
        if (isWriteToolResult(tool.output)) {
          return <ProposalCard key={`proposal-${tool.id}`} result={tool.output} isStreaming={isStreaming} />;
        }
        return <ReservationCards key={`reservations-${tool.id}`} reservations={reservations.get(tool.id) ?? []} />;
      })}
    </div>
  );
}

/**
 * El text de Miranda. El Markdown va dins d'un contenidor que no pot ser més ample que la
 * bombolla i que fa scroll horitzontal per dins: una taula ampla o una línia de codi llarga es
 * desplacen aquí, no a la pàgina (`MarkdownMessage` no es toca: s'embolcalla).
 *
 * Amb `stale`, el text és d'abans que la targeta del mateix missatge es resolgués: queda plegat
 * (`hidden`, fora de la vista i del lector de pantalla) fins que es desplega amb el control del
 * missatge (`BeforeDecisionToggle`), i desplegat surt atenuat.
 */
function AssistantText({
  id,
  text,
  stale,
  expanded,
}: {
  id: string;
  text: string;
  stale: boolean;
  expanded: boolean;
}) {
  return (
    <div
      id={id}
      className="min-w-0 max-w-full"
      data-testid="assistant-text"
      data-stale={stale ? 'true' : undefined}
      hidden={stale && !expanded}
    >
      <div className="min-w-0 max-w-full overflow-x-auto break-words">
        <MarkdownMessage content={text} className={cn(stale && 'text-muted-foreground')} />
      </div>
    </div>
  );
}

/**
 * «Text d'abans de la decisió · mostra»: el botó que desplega (o torna a plegar) els textos del
 * missatge escrits abans que la targeta es resolgués. Un sol control per missatge.
 */
function BeforeDecisionToggle({
  expanded,
  controls,
  onToggle,
}: {
  expanded: boolean;
  controls: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex min-h-10 items-center gap-1 self-start text-muted-foreground text-xs underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={onToggle}
      data-testid="before-decision-toggle"
    >
      <ChevronRight className={cn('size-3.5 shrink-0 transition-transform', expanded && 'rotate-90')} />
      {BEFORE_DECISION_LABEL} · {expanded ? 'amaga' : 'mostra'}
    </button>
  );
}

/**
 * El missatge de Miranda: text, eines i targetes. L'estat de cada targeta d'escriptura es llegeix
 * de la mateixa consulta que fa la targeta (`proposalStatusQuery`: mateixa clau, cap petició de
 * més; la resposta d'un clic també hi queda desada), per saber si el text és d'abans de la decisió.
 * També en reobrir una conversa desada: la consulta d'estat ho diu igual.
 */
function AssistantMessage({ segments, isStreaming }: { segments: MessageSegment[]; isStreaming: boolean }) {
  const [showBefore, setShowBefore] = useState(false);
  const idPrefix = useId();
  const writes = writeResultsOf(segments);
  // Un id, una consulta: dues tools del mateix torn poden tornar la mateixa proposta.
  const statusIds = [...new Set(writes.map(proposalStatusId).filter((id): id is string => !!id))];
  const statuses = useQueries({ queries: statusIds.map((id) => proposalStatusQuery(id)) });
  const stale = isTextFromBeforeDecision(
    writes.map((result) => {
      const id = proposalStatusId(result);
      return { result, current: id ? statuses[statusIds.indexOf(id)]?.data : undefined };
    }),
  );
  const reservations = messageReservations(segments);
  const textIdOf = (key: string) => `${idPrefix}-${key}`;
  const textKeys = segments.filter((segment) => segment.kind === 'text').map((segment) => segment.key);
  const firstTextKey = textKeys[0];

  return (
    <Message from="assistant" className="min-w-0 max-w-full">
      <MessageContent className="w-full">
        {segments.map((segment) => (
          <Fragment key={segment.key}>
            {segment.kind === 'text' && stale && segment.key === firstTextKey && (
              <BeforeDecisionToggle
                expanded={showBefore}
                controls={textKeys.map(textIdOf).join(' ')}
                onToggle={() => setShowBefore((value) => !value)}
              />
            )}
            {segment.kind === 'text' && (
              <AssistantText id={textIdOf(segment.key)} text={segment.text} stale={stale} expanded={showBefore} />
            )}
            {segment.kind === 'tools' && (
              <ToolsBlock tools={segment.tools} reservations={reservations} isStreaming={isStreaming} />
            )}
            {segment.kind === 'workflow' && <WorkflowApprovalCard pointer={segment.pointer} isStreaming={isStreaming} />}
            {segment.kind === 'card-action' && <CardActionNote text={segment.text} />}
          </Fragment>
        ))}
      </MessageContent>
    </Message>
  );
}

/**
 * El rastre d'una acció feta a una targeta (aprovar, rebutjar, desfer): una nota petita i
 * centrada, de color neutre i sense bombolla. No ho ha escrit la persona ni ho ha dit Miranda.
 */
export function CardActionNote({ text }: { text: string }) {
  return (
    <p
      role="note"
      className="mx-auto max-w-[85%] whitespace-pre-wrap break-words text-center text-muted-foreground text-xs"
      data-testid="card-action-note"
    >
      {text}
    </p>
  );
}

export interface RichMessageProps {
  message: MirandaUIMessage;
  /**
   * Miranda encara escriu aquest missatge. Mentre és `true`, els botons d'acció de les seves
   * targetes (aprovar, rebutjar, desfer) estan desactivats: el xat fa scroll al final a cada tros
   * i el botó es mouria sota el dit.
   */
  isStreaming?: boolean;
}

/**
 * Un missatge del xat ric: la bombolla de l'usuari, la nota d'una acció feta a una targeta, o el
 * text, les eines i les targetes de Miranda.
 */
export function RichMessage({ message, isStreaming = false }: RichMessageProps) {
  const segments = segmentMessage(message);
  if (segments.length === 0) return null;

  // Un missatge que només porta rastres no és de ningú: ni bombolla ni columna de Miranda.
  if (segments.every((segment) => segment.kind === 'card-action')) {
    return (
      <div className="flex w-full flex-col gap-1">
        {segments.map((segment) =>
          segment.kind === 'card-action' ? <CardActionNote key={segment.key} text={segment.text} /> : null,
        )}
      </div>
    );
  }

  // Un rol que el xat no pinta (`system`) sense cap rastre: res.
  if (message.role !== 'user' && message.role !== 'assistant') return null;

  if (message.role === 'user') {
    return (
      <Message from="user" className="min-w-0">
        <MessageContent>
          {segments.map((segment) =>
            segment.kind === 'text' ? (
              <p key={segment.key} className="min-w-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere]">
                {segment.text}
              </p>
            ) : null,
          )}
        </MessageContent>
      </Message>
    );
  }

  return <AssistantMessage segments={segments} isStreaming={isStreaming} />;
}
